import { toast } from "sonner";
import {
  bulkDeleteHandler,
  bulkExportHandler,
  bulkStatusUpdateHandler,
  type BulkActionConfig,
  type BulkActionMutations,
} from "./genericBulkActions.core";

// ============================================================================
// ENTITY-SPECIFIC BULK ACTION CONFIGURATIONS
// ============================================================================

/**
 * Create bulk actions configuration for tasks
 */
export function createTaskBulkActions(mutations: BulkActionMutations): BulkActionConfig[] {
  return [
    {
      id: "bulk_complete",
      label: "Mark Complete",
      icon: "check",
      handler: async (selectedItems, mutations, entityName) => {
        await bulkStatusUpdateHandler(selectedItems, mutations, entityName, "status", "completed", {
          completed_at: new Date().toISOString(),
        });
      },
    },
    {
      id: "bulk_assign",
      label: "Assign User",
      icon: "user",
      handler: async (selectedItems, mutations, entityName) => {
        // This would need a user selection modal
        // For now, just show a placeholder
        toast.info("Bulk assign feature coming soon");
      },
    },
    {
      id: "bulk_delete",
      label: "Delete",
      icon: "trash",
      variant: "destructive",
      handler: bulkDeleteHandler,
    },
    {
      id: "bulk_export",
      label: "Export",
      icon: "download",
      handler: bulkExportHandler,
    },
  ];
}

/**
 * Create bulk actions configuration for contacts
 */
export function createContactBulkActions(mutations: BulkActionMutations): BulkActionConfig[] {
  return [
    {
      id: "bulk_delete",
      label: "Delete",
      icon: "trash",
      variant: "destructive",
      handler: bulkDeleteHandler,
    },
    {
      id: "bulk_export",
      label: "Export",
      icon: "download",
      handler: bulkExportHandler,
    },
    {
      id: "bulk_tag",
      label: "Add Tags",
      icon: "tag",
      handler: async (selectedItems, mutations, entityName) => {
        toast.info("Bulk tag feature coming soon");
      },
    },
  ];
}

/**
 * Create bulk actions configuration for time entries
 */
export function createTimeEntryBulkActions(mutations: BulkActionMutations): BulkActionConfig[] {
  return [
    {
      id: "bulk_approve",
      label: "Approve",
      icon: "check",
      handler: async (selectedItems, mutations, entityName) => {
        await bulkStatusUpdateHandler(selectedItems, mutations, entityName, "status", "approved");
      },
    },
    {
      id: "bulk_reject",
      label: "Reject",
      icon: "x",
      variant: "destructive",
      handler: async (selectedItems, mutations, entityName) => {
        await bulkStatusUpdateHandler(selectedItems, mutations, entityName, "status", "rejected");
      },
    },
    {
      id: "bulk_delete",
      label: "Delete",
      icon: "trash",
      variant: "destructive",
      handler: bulkDeleteHandler,
    },
    {
      id: "bulk_export",
      label: "Export",
      icon: "download",
      handler: bulkExportHandler,
    },
  ];
}

/**
 * Create bulk actions configuration for social media posts.
 * Reuses the generic status-update / delete / export handlers (no hand-rolled
 * per-row logic): Cancel sets status → "canceled"; Delete + Export are generic.
 */
export function createSocialMediaPostBulkActions(
  mutations: BulkActionMutations
): BulkActionConfig[] {
  return [
    {
      id: "bulk_cancel",
      label: "Cancel",
      icon: "x",
      variant: "destructive",
      handler: async (selectedItems, mutations, entityName) => {
        await bulkStatusUpdateHandler(selectedItems, mutations, entityName, "status", "canceled");
      },
    },
    {
      id: "bulk_delete",
      label: "Delete",
      icon: "trash",
      variant: "destructive",
      handler: bulkDeleteHandler,
    },
    {
      id: "bulk_export",
      label: "Export",
      icon: "download",
      handler: bulkExportHandler,
    },
  ];
}

/**
 * Create bulk actions configuration for tickets
 */
export function createTicketBulkActions(mutations: BulkActionMutations): BulkActionConfig[] {
  return [
    {
      id: "bulk_close",
      label: "Close",
      icon: "check",
      handler: async (selectedItems, mutations, entityName) => {
        // Prefer the single-request batch endpoint when available (tickets): one
        // request, and the executor stamps closed_at on the status transition
        // itself. Fall back to the per-item loop for any entity without it.
        if (mutations.bulkUpdate) {
          const result = await mutations.bulkUpdate(
            selectedItems.map((item) => item.id),
            { status: "closed" }
          );
          if (result.failed > 0) {
            toast.warning(
              result.message ??
                `Closed ${result.successful} of ${result.total} ${entityName}; ${result.failed} failed.`
            );
          } else {
            toast.success(result.message ?? `Closed ${result.successful} ${entityName}`);
          }
          return;
        }
        await bulkStatusUpdateHandler(selectedItems, mutations, entityName, "status", "closed", {
          closed_at: new Date().toISOString(),
        });
      },
    },
    {
      id: "bulk_assign",
      label: "Assign",
      icon: "user",
      handler: async (selectedItems, mutations, entityName) => {
        toast.info("Bulk assign feature coming soon");
      },
    },
    {
      id: "bulk_delete",
      label: "Delete",
      icon: "trash",
      variant: "destructive",
      handler: bulkDeleteHandler,
    },
    {
      id: "bulk_export",
      label: "Export",
      icon: "download",
      handler: bulkExportHandler,
    },
  ];
}
